/**
 * Browse by state — spec §25, §48.
 *
 * Geography is a first-class dimension of this product, not a filter chip. A
 * buyer in Onitsha searching for a supplier "in Anambra" is being asked to sift
 * a whole state; this page is the entry into the LGA-level index that avoids
 * that.
 */

import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { MapPinIcon } from '@/components/ui/icons'
import { cx, EmptyState, Panel, SectionHeader, Skeleton } from '@/components/ui/primitives'
import { referenceApi } from '@/services/endpoints'
import { formatNumber } from '@/utils/format'

/** Nigeria's six geopolitical zones, in the conventional order. */
const ZONE_ORDER = [
  'North Central', 'North East', 'North West',
  'South East', 'South South', 'South West',
]

export function StatesPage() {
  const states = useQuery({ queryKey: ['states'], queryFn: referenceApi.states })

  if (states.isLoading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <Skeleton className="h-8 w-64" />
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 12 }).map((_, index) => (
            <Skeleton key={index} className="h-16" />
          ))}
        </div>
      </div>
    )
  }

  const rows = states.data ?? []
  const grouped = ZONE_ORDER.map((zone) => ({
    zone,
    states: rows.filter((state) => state.region === zone),
  })).filter((group) => group.states.length > 0)

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <h1 className="text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
        Businesses by state
      </h1>
      <p className="mt-2 max-w-2xl text-sm text-[var(--text-secondary)]">
        All 36 states and the Federal Capital Territory, grouped by geopolitical zone.
        Each state opens to its local government areas.
      </p>

      <div className="mt-8 space-y-8">
        {grouped.map((group) => (
          <section key={group.zone}>
            <SectionHeader title={group.zone} className="mb-3" />
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {group.states.map((state) => (
                <Link
                  key={state.slug}
                  to={`/states/${state.slug}`}
                  className="group panel flex items-center justify-between gap-3 p-3 transition-colors hover:border-[var(--accent-border)]"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-[var(--text-primary)] group-hover:text-[var(--accent-text)]">
                      {state.name}
                    </p>
                    <p className="truncate text-2xs text-[var(--text-tertiary)]">
                      {state.capital}
                    </p>
                  </div>
                  <span
                    className={cx(
                      'shrink-0 text-xs tabular',
                      state.business_count > 0
                        ? 'text-[var(--text-secondary)]'
                        : 'text-[var(--text-tertiary)]',
                    )}
                  >
                    {formatNumber(state.business_count)}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

export function StateDetailPage() {
  const { slug = '' } = useParams()
  const state = useQuery({
    queryKey: ['state', slug],
    queryFn: () => referenceApi.state(slug),
    enabled: Boolean(slug),
  })

  if (state.isLoading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-6 h-64" />
      </div>
    )
  }

  if (state.isError || !state.data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <EmptyState icon={<MapPinIcon size={18} />} title="State not found" />
      </div>
    )
  }

  const record = state.data

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <nav className="mb-3 flex items-center gap-1.5 text-xs text-[var(--text-tertiary)]">
        <Link to="/states" className="hover:text-[var(--accent-text)]">States</Link>
        <span>/</span>
        <span>{record.name}</span>
      </nav>

      <h1 className="text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
        Businesses in {record.name}
      </h1>
      <p className="mt-1.5 text-sm text-[var(--text-secondary)]">
        Capital {record.capital} · {record.region} ·{' '}
        <span className="tabular">{formatNumber(record.business_count)}</span> indexed
      </p>

      <Link
        to={`/search?state=${record.slug}`}
        className="mt-4 inline-flex items-center gap-1.5 text-sm text-[var(--accent-text)] hover:underline"
      >
        Search all businesses in {record.name}
      </Link>

      {record.major_cities.length > 0 ? (
        <section className="mt-8">
          <SectionHeader title="Major commercial centres" className="mb-3" />
          <div className="flex flex-wrap gap-2">
            {record.major_cities.map((city) => (
              <Link
                key={city.slug}
                to={`/search?state=${record.slug}&city=${city.slug}`}
                className="rounded-full border border-[var(--border-subtle)] bg-[var(--surface-panel)] px-3 py-1.5 text-xs text-[var(--text-secondary)] transition-colors hover:border-[var(--accent-border)] hover:text-[var(--accent-text)]"
              >
                {city.name}
                {city.business_count > 0 ? (
                  <span className="ml-1.5 tabular text-[var(--text-tertiary)]">
                    {city.business_count}
                  </span>
                ) : null}
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section className="mt-8">
        <SectionHeader
          title="Local government areas"
          description={`${record.local_governments.length} LGAs. A supplier listed only as "in ${record.name}" may be four hours away — narrow it down.`}
          className="mb-3"
        />
        <Panel padded={false}>
          <div className="grid grid-cols-2 divide-x divide-[var(--border-subtle)] sm:grid-cols-3 lg:grid-cols-4">
            {record.local_governments.map((lga) => (
              <Link
                key={lga.slug}
                to={`/search?state=${record.slug}&lga=${lga.slug}`}
                className="flex items-center justify-between gap-2 border-b border-[var(--border-subtle)] px-3 py-2 text-xs transition-colors hover:bg-[var(--surface-hover)]"
              >
                <span className="truncate text-[var(--text-secondary)]">{lga.name}</span>
                {lga.business_count > 0 ? (
                  <span className="shrink-0 tabular text-[var(--text-tertiary)]">
                    {lga.business_count}
                  </span>
                ) : null}
              </Link>
            ))}
          </div>
        </Panel>
      </section>
    </div>
  )
}
